import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { Loading } from '@/ui-legacy/Loading'
import { DocumentLandscapeData20Regular } from '@fluentui/react-icons'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { useCallback } from 'react'
import { DataAssetFormV2 } from '../../Forms/DataAssetFormV2'
import { toast } from 'sonner'

type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']
type MessagesDataAssetsDataAsset = RestSchemas['Messages.DataAssets.DataAsset']

export function DataAssetsList({
  entityType,
  applicationId,
}: {
  entityType: MessagesEntityTypesExistingEntityType
  applicationId: number
}) {
  const { getDataAssetApiListDataAssets } = useRestClient()
  const {
    data: dataAssets,
    isPending,
    isError,
  } = getDataAssetApiListDataAssets.useQuery({
    query: { applicationId: applicationId, entityTypeId: entityType.id },
  })

  const invalidateDataAssets = useCallback(
    () =>
      getDataAssetApiListDataAssets.invalidateQueries({
        parameters: { query: { applicationId: applicationId, entityTypeId: entityType.id } },
      }),
    [entityType.id, getDataAssetApiListDataAssets, applicationId]
  )

  if (isPending) return <Loading />
  if (isError) return <ErrorComponent />

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <h6 className="text-sm">Data Assets:</h6>
        <DataAssetFormV2
          onSubmit={invalidateDataAssets}
          selectedEntityType={entityType}
          sheetTitle="Add Data Asset"
          sheetTriggerTooltip="Add data asset"
          sheetDescription="Add a new data asset"
          sheetTrigger={
            <Button variant="outline" size="icon" className="size-6 rounded-full">
              <IconPlusOutline />
              <span className="sr-only">Add Data Asset</span>
            </Button>
          }
        />
      </div>
      <ul className="w-full divide-y divide-gray-100 font-light">
        {dataAssets.length > 0 ? (
          dataAssets.map((dataAsset) => (
            <ListItem
              key={dataAsset.id}
              dataAsset={dataAsset}
              entityType={entityType}
              invalidateDataAssets={invalidateDataAssets}
            />
          ))
        ) : (
          <div className="italic">...No data assets found...</div>
        )}
      </ul>
    </div>
  )
}

function ListItem({
  dataAsset,
  entityType,
  invalidateDataAssets,
}: {
  dataAsset: MessagesDataAssetsDataAsset
  entityType: MessagesEntityTypesExistingEntityType
  invalidateDataAssets: () => void
}) {
  const { deleteDataAssetApiDeleteTemplate } = useRestClient()
  const { mutate: deleteDataAsset } = deleteDataAssetApiDeleteTemplate.useMutation()

  const handleDelete = () => {
    deleteDataAsset(
      { body: dataAsset },
      {
        onSuccess: () => {
          toast.success('Data asset successfully deleted')
          invalidateDataAssets()
        },
        onError: () => toast.error('Error deleting data asset'),
      }
    )
  }

  return (
    <>
      <li key={dataAsset.id} className="flex items-center justify-between">
        <div className="flex shrink-0 items-center gap-2">
          <DocumentLandscapeData20Regular />
          {dataAsset.name}
        </div>
        <div className="flex items-center">
          <DataAssetFormV2
            onSubmit={invalidateDataAssets}
            selectedEntityType={entityType}
            selectedDataAsset={dataAsset}
            sheetTitle="Edit Data Asset"
            sheetTriggerTooltip="Edit"
            sheetDescription="Edit the data asset"
            sheetTrigger={
              <Button variant="ghost" size="icon" className="size-6">
                <IconEditOutline />
                <span className="sr-only">Edit Data Asset</span>
              </Button>
            }
          />
          <ConfirmDialog
            title="Confirm Delete"
            description="Are you sure you want to delete this data asset?"
            onConfirm={handleDelete}
            triggerTooltip="Delete"
            trigger={
              <Button size="icon" variant="ghost" className="h-6 w-6">
                <IconDeleteOutline size={16} />
              </Button>
            }
          />
        </div>
      </li>
    </>
  )
}
